import React, { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Copy, Send, Loader2 } from "lucide-react";
import OpenAI from "openai";
import ReactMarkdown from 'react-markdown';
import '../components/chatbot.css';

const Chatbot = () => {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef(null);

    const OPEN_API_KEY = import.meta.env.VITE_OPENAI_API_KEY;
    const ASSISTANT_ID = import.meta.env.VITE_SAARTHI_ASSISTANT_ID;

    const openai = new OpenAI({
        apiKey: OPEN_API_KEY,
        dangerouslyAllowBrowser: true,
    });

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleSendMessage = async () => {
        if (!input.trim()) return;

        setIsLoading(true);
        const userMessage = { role: "user", content: input };
        setMessages((prev) => [...prev, userMessage]);

        try {
            const run = await openai.beta.threads.createAndRun({
                assistant_id: ASSISTANT_ID,
                thread: {
                    messages: [{ role: "user", content: input }],
                },
            });

            let retrieveRun;
            do {
                retrieveRun = await openai.beta.threads.runs.retrieve(run.thread_id, run.id);
                await new Promise((resolve) => setTimeout(resolve, 1000));
            } while (retrieveRun.status !== "completed");

            const threadMessages = await openai.beta.threads.messages.list(run.thread_id);

            const assistantMessage = threadMessages.data
                .filter((msg) => msg.role === "assistant")
                .map((msg) => msg.content[0].text.value)
                .join("\n");

            // Remove source references using regex
            const cleanMessage = assistantMessage.replace(/【[\d:†source】]+/g, '');

            setMessages((prev) => [...prev, { role: "assistant", content: cleanMessage }]);
        } catch (error) {
            console.error("Error sending message:", error);
            setMessages((prev) => [
                ...prev,
                { role: "assistant", content: "Sorry, something went wrong." },
            ]);
        } finally {
            setIsLoading(false);
            setInput("");
        }
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
        } catch (err) {
            console.error("Failed to copy text: ", err);
        }
    };

    return (
        <div className="flex flex-col h-screen max-w-7xl mx-auto p-4 space-y-4">
            <Card className="flex-1">
                <CardHeader className="border-b">
                <CardTitle className="text-xl font-semibold flex items-center">
  <img 
    src="/sarrthiias-logo.png" 
    alt="Logo" 
    className="w-10 h-10 mr-4"
  />
  Saarthi AI Assistant
</CardTitle>


                </CardHeader>
                <ScrollArea className="h-[calc(100vh-12rem)] px-4">
                    <CardContent className="pt-4 pb-2">
                        {messages.length === 0 ? (
                            <div className="text-center text-muted-foreground pt-8">
                                Ask Any Customers Sales Doubts Here !
                            </div>
                        ) : (
                            messages.map((msg, index) => (
                                <div
                                    key={index}
                                    className={`group flex ${msg.role === "user" ? "justify-end" : "justify-start"
                                        } mb-4`}
                                >
                                    <div
                                        className={`relative max-w-[80%] px-4 py-2 rounded-lg ${msg.role === "user"
                                                ? "bg-black text-white"
                                                : "bg-gray-100"
                                            }`}
                                    >
                                        {msg.role === "assistant" ? (
                                            <div className="markdown-content">
                                                <ReactMarkdown>{msg.content}</ReactMarkdown>
                                            </div>
                                        ) : (
                                            <p className="whitespace-pre-wrap">{msg.content}</p>
                                        )}
                                        {msg.role === "assistant" && (
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="absolute -right-10 top-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                                onClick={() => copyToClipboard(msg.content)}
                                            >
                                                <Copy className="h-4 w-4" />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                        <div ref={messagesEndRef} />
                    </CardContent>
                </ScrollArea>
            </Card>
            <div className="flex gap-2">
                <Input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Type your message..."
                    className="flex-1"
                    disabled={isLoading}
                    onKeyPress={(e) => e.key === "Enter" && !e.shiftKey && handleSendMessage()}
                />
                <Button
                    onClick={handleSendMessage}
                    disabled={isLoading}
                    className="px-6 bg-black hover:bg-black/90"
                >
                    {isLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin text-white" />
                    ) : (
                        <Send className="h-4 w-4 text-white" />
                    )}
                </Button>
            </div>
        </div>
    );
};

export default Chatbot;